import {
  AddressDto,
  EmailAddressDto,
  GuideDashboardDto,
  InteractableDeleteResponseDto,
  PhoneNumberDto,
  RaceEthnicityDto,
  TeamDto,
  TeamHierarchyDto,
  UserDataViewDto,
  UserDto,
  UserRaceEthnicityDto,
  VerifiedResponseDto,
} from '@act/shared/data-transfer-objects';
import { EndpointDefinition } from '../utils/endpointDefinition';

export const userRequests = {
  verifyUser: EndpointDefinition.get<VerifiedResponseDto, null>({
    name: 'verifyUser',
    route: '/users/user/verify',
  }),

  getUserDetails: new EndpointDefinition<UserDto, null, { memberId: string }>({
    name: 'getUserDetails',
    route: '/users/:memberId',
    method: 'GET',
    defaults: { params: { ['include-details']: 'true' } },
  }),

  getMembersForGuide: EndpointDefinition.get<
    UserDataViewDto,
    { guideId: string; includeDetails?: boolean; includeSegments?: boolean }
  >({
    name: 'getGuideMembers',
    route: '/users',
  }),

  getUsersByIds: new EndpointDefinition<
    UserDataViewDto,
    null,
    { ids: string[]; includeDetails?: boolean }
  >({
    name: 'getUsersByIds',
    route: '/users',
    method: 'GET',
  }),

  getTeam: EndpointDefinition.get<
    UserDto[],
    { allViewable?: boolean; allCareStaff?: boolean }
  >({
    name: 'getTeam',
    route: '/users/team',
  }),

  getTeams: EndpointDefinition.get<TeamDto[], { allViewable?: boolean }>({
    name: 'getTeams',
    route: '/users/teams',
  }),

  getHierarchy: EndpointDefinition.get<TeamHierarchyDto[], null>({
    name: 'getHierarchy',
    route: '/users/hierarchy',
  }),

  getRaceEthnicityOptions: new EndpointDefinition<
    RaceEthnicityDto[],
    null,
    null
  >({
    name: 'getRaceEthnicityOptions',
    route: '/users/race-ethnicities',
    method: 'GET',
  }),

  getRaceEthnicityForUser: new EndpointDefinition<
    UserRaceEthnicityDto[],
    null,
    { memberId: string }
  >({
    name: 'getRaceEthnicityForUser',
    route: '/users/:memberId/race-ethnicities',
    method: 'GET',
  }),

  patchMemberInfo: new EndpointDefinition<
    UserDto,
    {
      namePronunciation: string;
      preferredName: string;
      pronouns: string;
      raceEthnicitySummary: string;
      reportedLanguage: string;
    },
    { memberId: string }
  >({
    name: 'patchUserInfo',
    route: '/users/:memberId',
    method: 'PATCH',
  }),

  putRaceEthnicityInfo: new EndpointDefinition<
    UserRaceEthnicityDto[],
    { raceEthnicityId: number; detail: string | null }[],
    { memberId: string }
  >({
    name: 'putRaceEthnicityInfo',
    route: '/users/:memberId/race-ethnicities',
    method: 'PUT',
  }),

  createPhoneNumber: new EndpointDefinition<
    PhoneNumberDto,
    {
      description: string;
      number: string;
      primary: boolean;
      carrier?: string;
      type?: string;
    },
    { userId: string }
  >({
    name: 'createPhoneNumber',
    route: '/interaction/phone-numbers',
    method: 'POST',
  }),

  updatePhoneNumber: new EndpointDefinition<
    PhoneNumberDto,
    { primary: boolean; description: string; carrier?: string; type?: string },
    { phoneId: number; userId: string }
  >({
    name: 'updatePhoneNumber',
    route: '/interaction/phone-numbers/:phoneId',
    method: 'PATCH',
  }),

  deletePhoneNumber: EndpointDefinition.delete<
    InteractableDeleteResponseDto,
    { phoneId: number; userId: string }
  >({
    name: 'deletePhoneNumber',
    route: '/interaction/phone-numbers/:phoneId',
  }),

  createEmailAddress: EndpointDefinition.post<
    EmailAddressDto,
    {
      email: string;
      description: string;
      primary?: boolean;
    },
    { memberId: string }
  >({
    name: 'createEmailAddress',
    route: '/interaction/email-address',
  }),

  updateEmailAddress: EndpointDefinition.patch<
    EmailAddressDto,
    { description: string; email: string; primary?: boolean },
    { id: number; memberId: string }
  >({
    name: 'updateEmailAddress',
    route: '/interaction/email-address/:id',
  }),

  deleteEmailAddress: EndpointDefinition.delete<
    InteractableDeleteResponseDto,
    { id: number; memberId: string }
  >({
    name: 'deleteEmailAddress',
    route: '/interaction/email-address/:id',
  }),

  createAddress: new EndpointDefinition<
    AddressDto,
    {
      description: string;
      line1: string;
      line2: string;
      city: string;
      state: string;
      zip: string;
      primary: boolean;
    },
    { userId: string }
  >({
    name: 'createAddress',
    route: '/interaction/addresses',
    method: 'POST',
  }),

  updateAddress: new EndpointDefinition<
    AddressDto,
    {
      description: string;
      line1: string;
      line2: string;
      city: string;
      state: string;
      zip: string;
      primary: boolean;
    },
    { addressId: number; userId: string }
  >({
    name: 'updateAddress',
    route: '/interaction/addresses/:addressId',
    method: 'PATCH',
  }),

  updatePrimaryAddress: new EndpointDefinition<
    AddressDto,
    null,
    { addressId: number; userId: string }
  >({
    name: 'updatePrimaryAddress',
    route: '/interaction/addresses/:addressId/primary',
    method: 'PATCH',
  }),

  deleteAddress: EndpointDefinition.delete<
    InteractableDeleteResponseDto,
    { addressId: number; userId: string }
  >({
    name: 'deleteAddress',
    route: '/interaction/addresses/:addressId',
  }),

  getGuideDashboard: EndpointDefinition.get<
    GuideDashboardDto,
    { guideId: string }
  >({
    name: 'getGuideDashboard',
    route: '/users/guide-dashboard/:guideId',
  }),
};
